"use client"

import { useTheme } from "@emotion/react"
import styled from "@emotion/styled"
import { CircularProgress, IconButton } from "@mui/material"
import { getWebPriorityAssetAssetNameDocuments } from "api"
import { useLocale, useTranslations } from "next-intl"
import Link from "next/link"
import { useEffect, useState } from "react"
import "swiper/css"
import "swiper/css/effect-coverflow"
import "swiper/css/effect-fade"
import "swiper/css/pagination"
import { Pagination } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import { DownloadIcon3, EyeIcon, FileIcon2, Mobile, NewText } from "ui"
import { sharedColors } from "ui/theme/colors"
import { Apartment, getUmbracoMediaUrl } from "utils"
import { chosenProjectType } from "utils/gtm"

/**
 * Convert a data URI to a Blob URL for efficient browser handling.
 * Data URIs freeze browsers when used directly in href for large files.
 */
const dataUriToBlobUrl = (dataUri: string): string => {
  const [header, base64Data] = dataUri.split(",")
  const mime = header.match(/data:(.*?);/)?.[1] || "application/octet-stream"
  const byteString = atob(base64Data)
  const ab = new ArrayBuffer(byteString.length)
  const ia = new Uint8Array(ab)
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i)
  }
  const blob = new Blob([ab], { type: mime })
  return URL.createObjectURL(blob)
}

const handleView = (url: string) => {
  if (url.startsWith("data:")) {
    const blobUrl = dataUriToBlobUrl(url)
    window.open(blobUrl, "_blank")
    setTimeout(() => URL.revokeObjectURL(blobUrl), 10000)
  } else {
    window.open(getUmbracoMediaUrl(url), "_blank")
  }
}

const handleDownload = async (url: string, fileName: string) => {
  try {
    let blobUrl: string
    if (url.startsWith("data:")) {
      blobUrl = dataUriToBlobUrl(url)
    } else {
      const response = await fetch(getUmbracoMediaUrl(url))
      const blob = await response.blob()
      blobUrl = URL.createObjectURL(blob)
    }
    const link = document.createElement("a")
    link.href = blobUrl
    link.download = fileName || "document.pdf"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(blobUrl)
  } catch {
    // Fallback: open in new tab if fetch fails
    window.open(url, "_blank")
  }
}

const BodyHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 16px;
  }
  .swiper-wrapper {
    margin-bottom: 2rem;
  }
  .swiper-pagination {
    bottom: 0;
    width: max-content;
    left: auto;
    right: auto;
    inset-inline-start: 0;
    .swiper-pagination-bullet {
      width: 6px;
      height: 6px;
      cursor: pointer;
      padding: 0;
      background-color: transparent;
      border-radius: 200px;
      border: 1px solid #000000;
    }
    .swiper-pagination-bullet-active {
      background: black;
    }
  }
`
const StyledDocs = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 24px;
  }
`

const Document = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 72px;
  cursor: pointer;
  width: 200px;
  padding: 24px 16px;
  background-color: #ffffff;
  border-radius: 16px;
  text-decoration: none;
  & > span {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  ${(props) => props.theme.breakpoints.down("sm")} {
    width: 100%;
  }
`
const DocumentNew = styled.div<{ index: number }>`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: space-between;
  width: 100%;
  height: 40px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.bulletsBackgroundColor4rentMarketing};
  margin-top: ${({ index }) => (index !== 0 ? "16px" : "0")};
`

const IconStyled = styled.div`
  height: 21.5px;
  width: 17.5px;
`

const DocsGrid = styled.div<{ forDrawer?: boolean }>`
  display: ${({ forDrawer }) => (forDrawer ? "flex" : "grid")};
  grid-template-columns: repeat(4, 1fr);
  flex-direction: ${({ forDrawer }) => forDrawer && "column"};
  padding-inline-end: ${({ forDrawer }) => (forDrawer ? "0" : "144px")};
  width: ${({ forDrawer }) => (forDrawer ? "100%" : "max-content")};
  gap: 20px;
  ${(props) => props.theme.breakpoints.down("md")} {
    display: ${({ forDrawer }) => (forDrawer ? "flex" : "none")};
  }
`

const IconsWrapped = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: max-content;
`

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`

export const getFileName = (url: string) => {
  // Handle base64 data URIs — show format extension
  if (url.startsWith("data:")) {
    const mime = url.split(";")[0]?.replace("data:", "") || ""
    if (mime.includes("pdf")) return "מסמך.pdf"
    if (mime.includes("jpeg") || mime.includes("jpg")) return "מסמך.jpg"
    if (mime.includes("png")) return "מסמך.png"
    if (mime.includes("svg")) return "מסמך.svg"
    if (mime.includes("zip")) return "מסמך.zip"
    if (mime.includes("msword") || mime.includes("doc")) return "מסמך.doc"
    return "מסמך"
  }

  const startName = url.split("/").pop()?.replace(".pdf", "")
  if (!startName) {
    return ""
  }
  let fileName = startName?.slice(0, 25)

  if (fileName?.endsWith("-")) {
    fileName = fileName.slice(0, -1)
  }
  if (fileName?.length < startName?.length) {
    fileName += "..."
  }
  return fileName
}

type DocItem = { url: string; name: string }

function useAssetDocuments(assetName?: string, locale?: string) {
  const [docs, setDocs] = useState<DocItem[]>([])
  const [loading, setLoading] = useState(false)
  const [fetched, setFetched] = useState(false)

  useEffect(() => {
    if (!assetName || fetched) return
    setLoading(true)
    getWebPriorityAssetAssetNameDocuments(assetName, locale)
      .then((res) => {
        const body = res.data?.body
        if (!body) return
        const items: DocItem[] = []
        if (body.rentalAgreement) {
          items.push({
            url: body.rentalAgreement,
            name: locale === "en" ? "Rental Agreement.pdf" : "הסכם שכירות לדוגמה.pdf",
          })
        }
        if (body.architecturalPlan) {
          items.push({
            url: body.architecturalPlan,
            name: locale === "en" ? "Floor Plan.pdf" : "תוכנית דירה.pdf",
          })
        }
        setDocs(items)
      })
      .catch(() => {
        // Silently fail — no documents available
      })
      .finally(() => {
        setLoading(false)
        setFetched(true)
      })
  }, [assetName, locale, fetched])

  return { docs, loading }
}

export default function ApartmentDocs({
  apartment,
  newStyle = false,
}: {
  apartment: Apartment
  newStyle?: boolean
}) {
  const { colors } = useTheme()
  const locale = useLocale()
  const t = useTranslations("AccordionDetails")
  const { docs: onDemandDocs, loading } = useAssetDocuments(apartment.assetName, locale)

  // Merge: Umbraco-provided contracts (if any) + on-demand Priority docs
  const allDocs: DocItem[] = [
    ...apartment.uploadContracts.map((url, i) => ({
      url,
      name: apartment.contractNames?.[i] || getFileName(url),
    })),
    ...onDemandDocs,
  ]

  if (loading) {
    return (
      <LoadingWrapper>
        <CircularProgress size={24} />
      </LoadingWrapper>
    )
  }

  if (allDocs.length === 0) return null

  return (
    <StyledDocs>
      {newStyle && (
        <NewText
          component="p"
          desktopFontSize={20}
          desktopLineHeight={"1.2"}
          desktopFontWeight={700}
          mobileFontSize={18}
          mobileFontWeight={700}
          color="#000"
        >
          {t("documents")}
        </NewText>
      )}
      <BodyHeader>
        <DocsGrid forDrawer={newStyle}>
          {allDocs.map((doc, i) => {
            return newStyle ? (
              <DocumentNew key={i} index={i}>
                <NewText
                  component={"span"}
                  desktopFontSize={16}
                  fontWeight={300}
                  desktopLineHeight={"150%"}
                  color={colors.newBlack}
                >
                  {doc.name}
                </NewText>
                <IconsWrapped>
                  <IconButton
                    onClick={() => {
                      chosenProjectType(doc.name, apartment?.propertyTitle, "")
                      handleView(doc.url)
                    }}
                  >
                    <EyeIcon color={colors.newBlack} />
                  </IconButton>
                  <IconButton
                    onClick={() =>
                      handleDownload(
                        doc.url,
                        doc.name.endsWith(".pdf") ? doc.name : doc.name + ".pdf",
                      )
                    }
                  >
                    <DownloadIcon3 color={colors.newBlack} />
                  </IconButton>
                </IconsWrapped>
              </DocumentNew>
            ) : (
              <Document
                key={i}
                href={doc.url.startsWith("data:") ? "#" : getUmbracoMediaUrl(doc.url)}
                target="_blank"
                onClick={(e) => {
                  if (doc.url.startsWith("data:")) {
                    e.preventDefault()
                    handleView(doc.url)
                  }
                  chosenProjectType(doc.name, apartment?.propertyTitle, "")
                }}
              >
                <IconStyled>
                  <FileIcon2 />
                </IconStyled>
                <NewText
                  component={"span"}
                  desktopFontSize={16}
                  desktopLineHeight={"23.04px"}
                  color={sharedColors.black}
                  borderBottom={"1px solid black"}
                >
                  {doc.name}
                </NewText>
              </Document>
            )
          })}
        </DocsGrid>
        {!newStyle && (
          <Mobile>
            <Swiper
              key={1}
              style={{ padding: 0 }}
              pagination={true}
              modules={[Pagination]}
              slidesPerView={1.4}
              spaceBetween={16}
            >
              {allDocs.map((doc, i) => (
                <SwiperSlide key={i}>
                  <Document
                    href={doc.url.startsWith("data:") ? "#" : getUmbracoMediaUrl(doc.url)}
                    target="_blank"
                    onClick={(e) => {
                      if (doc.url.startsWith("data:")) {
                        e.preventDefault()
                        handleView(doc.url)
                      }
                      chosenProjectType(doc.name, apartment?.propertyTitle, "")
                    }}
                  >
                    <IconStyled>
                      <FileIcon2 />
                    </IconStyled>
                    <NewText
                      component={"span"}
                      desktopFontSize={16}
                      desktopLineHeight={"23.04px"}
                      color={sharedColors.black}
                      borderBottom={"1px solid black"}
                    >
                      {doc.name}
                    </NewText>
                  </Document>
                </SwiperSlide>
              ))}
            </Swiper>
          </Mobile>
        )}
      </BodyHeader>
    </StyledDocs>
  )
}
